// @ts-nocheck - untyped
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockIcon from '@mui/icons-material/Lock';
import Notification from '../../Layout/Notification';
import UserManagementService from '../../../Services/UserManagementService';
import ConfirmationDialog from './ConfirmationDialog';
import { useTheme } from '@mui/material/styles';
import { validateUserData } from '../../../Utils/formValidation';

interface Tenant {
  name?: string;
  tenantKey?: string;
}

interface UserRecord {
  login?: string;
  activated?: boolean;
  locked?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  tenant?: Tenant;
  authorities?: string[];
}

interface UserDetailsProps {
  open: boolean;
  user: UserRecord | null;
  onClose: () => void;
  onUpdateData: () => void;
}

type ApiError = { response?: { data?: { description?: string } }; message?: string };
function getErrorMessage(err: unknown): string {
  const e = err as ApiError;
  return e?.response?.data?.description || e?.message || 'Unknown error';
}

const UserDetails = ({ open, user, onClose, onUpdateData }: UserDetailsProps) => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const { activated, locked, firstName, lastName, email } = user || {};
  const [formData, setFormData] = useState({
    active: activated || false,
    locked: locked || false,
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
  });
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string[]>([]);
  const { roles, loadingRoles, errorRoles } = UserManagementService.useRoles();
  const [tenant, setTenant] = useState<Tenant>({ name: '', tenantKey: '' });
  const { tenants, loadingTenants, errorTenants } = UserManagementService.useTenants();
  const [loading, setLoading] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [createUpdateConfirmationOpen, setCreateUpdateConfirmationOpen] = useState(false);
  const [unlockConfirmationOpen, setUnlockConfirmationOpen] = useState(false);
  const [lockDuration, setLockDuration] = useState<number | null>(null);

  const isDataLoading = () => loadingTenants || loadingRoles || loading;

  const handleFormChange = ({
    target,
  }: {
    target: { name: string; value: string; type?: string; checked?: boolean };
  }) => {
    const { name, value, type, checked } = target;
    const val = type === 'checkbox' ? checked : value;
    setFormData(prevData => ({ ...prevData, [name]: val }));
  };

  const handleUnlockConfirm = async () => {
    setUnlockConfirmationOpen(false);
    setLoading(true);
    try {
      await UserManagementService.unlockUser(user.login);
      setFormData({ ...formData, locked: false });
      setLockDuration(null);
      Notification.showSuccess(`User ${user.login} has been unlocked successfully.`);
      onUpdateData();
    } catch (error) {
      Notification.showError(`Failed to unlock user: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchLockDuration = async () => {
    if (user && user.locked) {
      try {
        const duration = await UserManagementService.getLockDuration(user.login);
        setLockDuration(duration);
      } catch (error) {
        console.error('Failed to fetch lock duration:', error);
      }
    }
  };

  const formatLockDuration = (seconds: number | null) => {
    if (!seconds || seconds <= 0) return null;
    const minutes = Math.floor(seconds / 60);
    return minutes > 0 ? `${minutes} minutes` : 'less than one minute';
  };

  const generateRandomPassword = () => {
    const generateRandomChar = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digitChars = '0123456789';
    const specialChars = '!@#$%^&*()_-+=<>?';
    const guaranteedChars = [
      generateRandomChar(lowercaseChars),
      generateRandomChar(uppercaseChars),
      generateRandomChar(digitChars),
      generateRandomChar(specialChars),
    ];
    const allChars = lowercaseChars + uppercaseChars + digitChars + specialChars;
    const remainingLength =
      Math.max(12, Math.floor(Math.random() * (24 - 11 + 1)) + 11) - guaranteedChars.length;
    const randomChars = Array.from({ length: remainingLength }, () => generateRandomChar(allChars));
    const passwordChars = [...guaranteedChars, ...randomChars].sort(() => 0.5 - Math.random());
    setPassword(passwordChars.join(''));
  };

  const handleChangeTenant = (e: { target: { value: string } }) => {
    const selectedTenant = tenants.find((t: Tenant) => t.tenantKey === e.target.value);
    if (selectedTenant) setTenant(selectedTenant);
  };

  const handleChangeRoles = ({ target }: { target: { value: string | string[] } }) => {
    const { value } = target;
    setRole(Array.isArray(value) ? value : [value]);
  };

  const handleCreateUpdateButtonClick = () => {
    if (validateUserData(user, formData, tenant, password)) {
      setCreateUpdateConfirmationOpen(true);
    }
  };

  const handleCreateUpdateConfirmationConfirm = async () => {
    setLoading(true);
    setCreateUpdateConfirmationOpen(false);
    try {
      await (user ? handleUserUpdate() : handleUserCreation());
      onUpdateData();
      onClose();
    } catch (error) {
      Notification.showError(
        getErrorMessage(error) || 'An unexpected error occurred. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUserCreation = async () => {
    const newUser = await UserManagementService.createUser({
      activated: formData.active,
      locked: formData.locked,
      login: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password,
      tenant: { name: tenant.name, tenantKey: tenant.tenantKey },
      authorities: role,
    });
    Notification.showSuccess(`User ${newUser.login} created successfully.`);
  };

  const handleUserUpdate = async () => {
    const userPayload: UserRecord & { password?: string } = {
      login: user.login,
      activated: formData.active,
      locked: formData.locked,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      tenant: { name: tenant.name, tenantKey: tenant.tenantKey },
      authorities: role,
    };
    if (password) userPayload.password = password;
    const updatedUser = await UserManagementService.updateUser(userPayload);
    Notification.showSuccess(`User ${updatedUser.login} updated successfully.`);
  };

  const handleDeleteConfirmationDelete = async () => {
    setDeleteConfirmationOpen(false);
    setLoading(true);
    try {
      if (user) {
        await UserManagementService.deleteUser(user.login);
        Notification.showSuccess('User deleted successfully.');
        onUpdateData();
        onClose();
      }
    } catch (error) {
      Notification.showError(`Error deleting user: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(false);
    if (errorTenants) Notification.showError(`Error fetching tenants: ${errorTenants.message}`);
    if (errorRoles) Notification.showError(`Error fetching roles: ${errorRoles.message}`);

    if (user) {
      setFormData({
        active: user.activated || false,
        locked: user.locked || false,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
      setTenant(user.tenant || { name: '', tenantKey: '' });
      setRole(user.authorities || []);
      setPassword('');
      if (user.locked) fetchLockDuration();
      else setLockDuration(null);
    } else {
      setFormData({ active: false, locked: false, firstName: '', lastName: '', email: '' });
      setTenant({ name: '', tenantKey: '' });
      setRole([]);
      setPassword('');
      setLockDuration(null);
    }
    // eslint-disable-next-line
  }, [user, open, errorTenants, errorRoles]);

  const labelSx = {
    textAlign: 'left',
    fontSize: '13px',
    color: 'var(--t-fg)',
    mb: 0.5,
    fontWeight: 500,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'var(--t-surface)',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user ? 'Edit User' : 'Create User'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user ? 'Update user information and permissions' : 'Create a new user account'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'var(--t-fg-muted)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5, pb: 1, position: 'relative' }}>
          {isDataLoading() && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.6)',
                zIndex: 10,
                borderRadius: 3,
              }}
            >
              <div className="spinner" />
            </Box>
          )}

          {/* Active / Lock status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
              alignItems: 'center',
              p: 1.5,
              border: '1px solid var(--t-border)',
              borderRadius: '8px',
              backgroundColor: 'var(--t-surface-subtle)',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleFormChange}
                  name="active"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: ACCENT,
                    },
                  }}
                />
              }
              label={<Typography sx={{ fontWeight: 500, fontSize: 14 }}>Active</Typography>}
              labelPlacement="end"
            />
            {user && formData.locked && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ color: theme.tokens.danger, fontSize: 18 }} />
                  <Typography sx={{ color: theme.tokens.danger, fontSize: 13, fontWeight: 500 }}>
                    Locked - unlocks in {formatLockDuration(lockDuration)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setUnlockConfirmationOpen(true)}
                  sx={{
                    backgroundColor: ACCENT,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    px: 2,
                    '&:hover': { backgroundColor: theme.tokens.accentHover },
                  }}
                >
                  Unlock Now
                </Button>
              </Box>
            )}
          </Box>

          {/* Names */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <InputLabel htmlFor="firstName" sx={labelSx}>
                First Name *
              </InputLabel>
              <TextField
                hiddenLabel
                id="firstName"
                name="firstName"
                size="small"
                value={formData.firstName || ''}
                onChange={handleFormChange}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <InputLabel htmlFor="lastName" sx={labelSx}>
                Last Name *
              </InputLabel>
              <TextField
                hiddenLabel
                id="lastName"
                name="lastName"
                size="small"
                value={formData.lastName || ''}
                onChange={handleFormChange}
                fullWidth
              />
            </Box>
          </Box>

          {/* Email */}
          <Box sx={{ mb: 2 }}>
            <InputLabel htmlFor="email" sx={labelSx}>
              Email Address *
            </InputLabel>
            <TextField
              hiddenLabel
              id="email"
              name="email"
              size="small"
              value={formData.email || ''}
              onChange={handleFormChange}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: user ? 'var(--t-surface-subtle)' : undefined,
                },
              }}
            />
          </Box>

          {/* Password */}
          <Box sx={{ mb: 2 }}>
            <InputLabel htmlFor="password" sx={labelSx}>
              {user ? 'Password' : 'Password *'}
            </InputLabel>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                hiddenLabel
                id="password"
                name="password"
                size="small"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={user ? 'Leave empty to keep current' : ''}
                sx={{ flex: 1 }}
              />
              <Button
                type="button"
                variant="contained"
                onClick={generateRandomPassword}
                startIcon={<VpnKeyIcon sx={{ fontSize: 16 }} />}
                sx={{
                  backgroundColor: ACCENT,
                  textTransform: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  '&:hover': { backgroundColor: theme.tokens.accentHover },
                }}
              >
                Generate
              </Button>
            </Box>
          </Box>

          {/* Tenant */}
          <Box sx={{ mb: 2 }}>
            <InputLabel htmlFor="tenant" sx={labelSx}>
              Tenant *
            </InputLabel>
            <Select
              required
              id="tenant"
              name="tenant"
              size="small"
              fullWidth
              value={tenant?.tenantKey || ''}
              onChange={handleChangeTenant}
              sx={{ textAlign: 'left' }}
            >
              {tenants.map((t: Tenant) => (
                <MenuItem key={t.tenantKey} value={t.tenantKey}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Roles */}
          <Box sx={{ mb: 1 }}>
            <InputLabel htmlFor="roles" sx={labelSx}>
              Roles
            </InputLabel>
            <Select
              id="roles"
              name="roles"
              multiple
              fullWidth
              size="small"
              MenuProps={{ style: { maxHeight: 400 } }}
              value={role}
              onChange={handleChangeRoles}
              input={<OutlinedInput id="select-multiple-chip" />}
              renderValue={selected => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxHeight: 80,
                    overflowY: 'auto',
                  }}
                >
                  {(selected as string[]).map((value: string) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {roles.map((r: { id: string | number; name: string }) => (
                <MenuItem key={r.id} value={r.name}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {user ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeleteConfirmationOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete User
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'var(--t-fg-muted)',
                borderColor: 'var(--t-border)',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUpdateButtonClick}
              sx={{
                backgroundColor: ACCENT,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: theme.tokens.accentHover },
              }}
            >
              {user ? 'Save Changes' : 'Create User'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={createUpdateConfirmationOpen}
        onClose={() => setCreateUpdateConfirmationOpen(false)}
        onConfirm={handleCreateUpdateConfirmationConfirm}
        title={`Confirm ${user ? 'Update' : 'Creation'}`}
        description={`Are you sure you want to ${user ? 'update' : 'create'} this user?`}
      />
      {user && (
        <ConfirmationDialog
          open={deleteConfirmationOpen}
          onClose={() => setDeleteConfirmationOpen(false)}
          onConfirm={handleDeleteConfirmationDelete}
          title="Confirm Deletion"
          description="Are you sure you want to delete this user?"
        />
      )}
      {user && formData.locked && (
        <ConfirmationDialog
          open={unlockConfirmationOpen}
          onClose={() => setUnlockConfirmationOpen(false)}
          onConfirm={handleUnlockConfirm}
          title="Confirm Unlock"
          description={`Are you sure you want to unlock user ${user.login}? This will allow the user to log in immediately.`}
        />
      )}
    </>
  );
};

export default UserDetails;
